/*
 *    Controller to show a user billing dashboard
 *    Triggered via:
 *        Radio.request(
 *          'show_billing',
 *        );
*/

import _ from 'underscore';
import moment from 'moment';
import Handlebars from 'handlebars';
import DashboardLayout from '@/views/dashboard/layout';
import DashboardGroup from '@/views/dashboard/group';
import Radio from 'backbone.radio';
import BreadcrumbNavLayout from '@/views/breadcrumb_nav_layout';
import BalanceComponent from '@/views/dashboard/billing/balance';
import CellularDevices from '@/views/dashboard/billing/cellular_devices';
import PaymentMethod from '@/views/dashboard/billing/payment_method';
import CurrentCharges from '@/views/dashboard/billing/pending_charges';
import PaymentHistory from '@/views/dashboard/billing/invoices';
import Backbone from 'backbone';
import Api from '@/lib/api/api';

var globalCh = Radio.channel('global');
var routerCh = Radio.channel('router');
var apiCh = Radio.channel('api');

let DashboardLayoutTemplate = `<div class="container-fluid account-billing-layout">
    <div class="row header">
        <div class="panel">
            <div class="panel-body col-xs-12 header">
                {{#if credit_warning}}
                <p class="alert alert-warning credit-warning" role="alert">
                    <i class="fa fa-exclamation-triangle warning-copy-icon"></i> <strong>Set Payment Method </strong>
                    to start using cellular devices. You will be charged once a month based on cellular device connectivity and bandwidth usage.
                </p><!-- no payment info -->
                {{/if}}
                <div class="col-md-6 col-xs-6">
                    <h1 class="title">Account Billing</h1>
                </div>
                <div class="col-md-6 col-xs-6 text-right">
                    {{#if features.show_billing}}
                        {{#if show_link_card_button}}
                            <button type="button" class="link-card btn btn-primary">Link your Card</button>
                        {{/if}}
                        {{#if features.show_billing_payment}}
                            <button type="button" class="make-payment btn btn-primary">Make a Payment</button>
                        {{/if}}
                    {{/if}}
                </div>
            </div>
        </div>
    </div>
</div>
<div class="container-fluid scrollable">
    <div class="group-container container">
        {{! Region elements added here dynamically by view }}
    </div>
</div>`;


const BALANCE_THRESHOLD = -20;

/**
 * Cards accepted via stripe: https://stripe.com/docs/testing
 * Pass this object into views where you need to display a credit card icon
 */
const cards = {
    'visa': 'visa',
    'mastercard': 'mastercard',
    'amex': 'amex',
    'discover': 'discover',
    'diners': 'diners-club',
    'jcb': 'jcb',
};

var BillingDashboard = DashboardLayout.extend({
    id: 'account-billing-layout',
    template: Handlebars.compile(DashboardLayoutTemplate),
    ui: {
        group_container: '.group-container',
        link_card_button: '.link-card',
        credit_warning: '.credit-warning',
        make_payment_button: '.make-payment',
    },
    events: {
        'click @ui.link_card_button': 'show_link_card_dialog',
        'click @ui.make_payment_button': 'show_make_payment_dialog',
    },
    modelEvents: {
        'add': 'render',
        'billing_change': 'reload'
    },
    reload: function() {
        let primary = globalCh.request('primary_account');
        globalCh.request('show_billing', primary);
    },
    templateContext: function() {
        var current_month_year = moment(new Date).format('MMMM YYYY');
        var show_link_card_button;

        // Determine which button to show - no card then show link card button
        show_link_card_button = (this.getOption('payment_data').get('card_type')) ? false : true;
        var show_negative_balance_alert = (this.getOption('payment_data').get('balance') <= BALANCE_THRESHOLD) ? true : false;
        const app_options = globalCh.request('app_options');
        return {
            date: current_month_year,
            features: app_options.features,
            credit_warning: (this.getOption('payment_data').has('card_type')) ? false : true,
            show_link_card_button,
            show_negative_balance_alert,
        };
    },
    show_link_card_dialog: function(){
        globalCh.request('show_stripe_dialog');
    },
    show_make_payment_dialog: function() {
        // removed from initial billing release
        globalCh.request('show_confirm_and_pay_dialog', cards);
    }
});

function dashboard(account) {
    var active_account = globalCh.request('active_account');
    // get dashboard data
    var primary_account = globalCh.request('primary_account');
    var payment_history = apiCh.request('payment');

    var d = new Date();
    var dashboard_date = new Date( Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 11, 59, 59 ) );
    var PaymentModel = Backbone.Model.extend({
        defaults: {
            id: null,
        },
        url: Api.urls.billing(),
        sync: Api.sync
    });

    var current_invoice = apiCh.request('invoice', { id: 'current-charges' });

    var payment_data = new PaymentModel();
    
    var available_groups = [
            // Group 1 - mini modules
            {
                columns: 8,
                widgets: [
                {
                    view: BalanceComponent,
                    options: {
                        columns: 4,
                        model: active_account,
                        payment_data,
                    }
                },
                {
                    view: CellularDevices,
                    options: {
                        columns: 4,
                        model: active_account,
                        payment_data,
                    }
                }
                ]
            },
            // Group 2 - modules // needs a collection of history from API
            {
                columns: 12,
                widgets: [
                {
                    view: CurrentCharges,
                    options: {
                        columns: 12,
                        model: current_invoice,
                        payment_history,
                    }
                }
                ]
            },
            // Group 3 - modules
            {
                columns: 12,
                widgets: [
                {
                    view: PaymentHistory,
                    options: {
                        columns: 12,
                        model: active_account,
                        payment_history,
                    }
                }

                ]
            }
        ];

        const app_options = globalCh.request('app_options');

        if ( app_options.features.show_billing ) {
            available_groups.push(
            {
                columns: 12,
                widgets: [
                {
                    view: PaymentMethod,
                    options: {
                        columns: 12,
                        model: active_account,
                        payment_data,
                        cards,
                    }
                },
                ]
            }
            );
        }

        var active_groups = [];
        var data_sources = [];

        _(available_groups).each(function(group) {
            var active_widgets = [];

            _(group.widgets).each(function(widget) {
                if (active_account.permits(primary_account, widget.view.prototype.required_permission)) {
                    active_widgets.push(new widget.view(widget.options));

                    if (!widget.options) { return; }
                    // Keep track of the model and/or collection so we can fetch it
                    if (widget.options.model) { data_sources.push(widget.options.model); }
                    if (widget.options.collection) { data_sources.push(widget.options.collection); }
                }
            });

            if (active_widgets.length === 0) { return; }

            group.widgets = active_widgets;

            active_groups.push(new DashboardGroup(group));
        });

        // Abort if we don't have any widgets to show (due to permissions),
        // or if the active account doesn't have ops
        if (active_groups.length === 0 ) {
            globalCh.request('home');
            return;
        }

        // Sync whatever models/collections we're using for widget data
        _.chain(data_sources)
            .uniq()
            .each(function(source) {
                // getUTCMonth is zero based, need to convert it to a non-zero based month for API to understand
                // the month we are asking for
                if ( source.type === 'devicegroup_bandwidth' ){
                    var end_date = (dashboard_date.getUTCMonth() + 1) + '/' + dashboard_date.getUTCDate() + '/' +  dashboard_date.getUTCFullYear();
                    var start_date = null;
                    source.fetch_day(start_date, end_date, 'days').then(function() { source.trigger('change'); });
                } else {
                source.fetch();
                }
        });

        // Show view in app container region after we get payment data
        var app_container = globalCh.request('app_layout'); 
        payment_data.fetch()
            .always(function(r){
                app_container.showChildView('container', new BillingDashboard({
                    model: active_account,
                    groups: active_groups,
                    new_left_nav: true,
                    payment_data: payment_data,
                }));
            });

        globalCh.request('show_secondary_nav', { empty: true });

        globalCh.request('show_profile_nav', { selected: 'account-billing'});

        routerCh.request('set_location', 'billing', active_account);

        app_container.showChildView('main_nav', new BreadcrumbNavLayout());
        globalCh.request('changeActiveAccount', account, function () {
            globalCh.request('set_selection', {
                type: 'app',
                id: 'home'
            });
        });

}

export default dashboard;
